import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Type, Heading, ImageIcon, Video, Quote, Code, Minus,
  Images, Headphones, BarChart, AlertCircle, Link as LinkIcon,
  GripVertical, Plus, Trash2, ChevronUp, ChevronDown
} from 'lucide-react';

const BLOCK_ICONS = {
  text: Type, heading: Heading, image: ImageIcon, video: Video,
  quote: Quote, embed: Code, divider: Minus, gallery: Images,
  audio: Headphones, data_viz: BarChart, callout: AlertCircle, related: LinkIcon
};

const BLOCK_LABELS = {
  text: 'Text', heading: 'Heading', image: 'Image', video: 'Video',
  quote: 'Pull Quote', embed: 'Embed', divider: 'Divider', gallery: 'Gallery',
  audio: 'Audio', data_viz: 'Data Viz', callout: 'Callout', related: 'Related'
};

const CALLOUT_STYLES = {
  info:    { bg: '#EFF6FF', border: '#3B82F6', icon: '💡' },
  warning: { bg: '#FFFBEB', border: '#F59E0B', icon: '⚠️' },
  success: { bg: '#F0FDF4', border: '#22C55E', icon: '✅' },
  error:   { bg: '#FFF1F2', border: '#F43F5E', icon: '🚫' },
};

export function BlockEditor({ blocks = [], onChange, onSave }) {
  const [showAddMenu, setShowAddMenu] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOver, setDragOver]  = useState(null);
  const dragNode = useRef(null);

  // ── Drag-and-drop reorder ────────────────────────────────
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    dragNode.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    setTimeout(() => { if (dragNode.current) dragNode.current.style.opacity = '0.4'; }, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragIndex) setDragOver(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) return;
    const updated = [...blocks];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    onChange(updated);
    setDragIndex(null);
    setDragOver(null);
  };

  const handleDragEnd = () => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    setDragIndex(null);
    setDragOver(null);
    dragNode.current = null;
  };

  // ── Block CRUD ───────────────────────────────────────────
  const addBlock = useCallback((type, afterIndex) => {
    const newBlock = { type, content: '', id: `block-${Date.now()}-${Math.random().toString(36).slice(2)}`, metadata: {} };
    const updated = [...blocks];
    updated.splice(afterIndex + 1, 0, newBlock);
    onChange(updated);
    setShowAddMenu(null);
  }, [blocks, onChange]);

  const updateBlock = useCallback((index, field, value) => {
    const updated = [...blocks];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  }, [blocks, onChange]);

  const removeBlock = useCallback((index) => {
    onChange(blocks.filter((_, i) => i !== index));
  }, [blocks, onChange]);

  const moveBlock = useCallback((index, dir) => {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    const updated = [...blocks];
    [updated[index], updated[newIdx]] = [updated[newIdx], updated[index]];
    onChange(updated);
  }, [blocks, onChange]);

  // ── Gallery block: multi-URL input ───────────────────────
  const addGalleryImage = (index) => {
    const current = blocks[index].images || [];
    updateBlock(index, 'images', [...current, { url: '', caption: '' }]);
  };

  const updateGalleryImage = (blockIndex, imgIndex, field, value) => {
    const updated = [...blocks];
    const images = [...(updated[blockIndex].images || [])];
    images[imgIndex] = { ...images[imgIndex], [field]: value };
    updated[blockIndex] = { ...updated[blockIndex], images };
    onChange(updated);
  };

  // ── Block renderer ───────────────────────────────────────
  const renderBlockEditor = (block, index) => {
    switch (block.type) {
      case 'text':
        return (
          <Textarea
            value={block.content || ''}
            onChange={e => updateBlock(index, 'content', e.target.value)}
            placeholder="Write your text here..."
            className="min-h-[100px] border-0 focus-visible:ring-0 resize-none text-base leading-7"
            data-testid={`block-text-${index}`}
          />
        );

      case 'heading':
        return (
          <div className="space-y-2">
            <Select value={block.level || 'h2'} onValueChange={v => updateBlock(index, 'level', v)}>
              <SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
                <SelectItem value="h4">H4</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={block.content || ''}
              onChange={e => updateBlock(index, 'content', e.target.value)}
              placeholder="Heading text..."
              className={`border-0 focus-visible:ring-0 font-bold news-masthead ${block.level === 'h3' ? 'text-lg' : block.level === 'h4' ? 'text-base' : 'text-2xl'}`}
            />
          </div>
        );

      case 'image':
        return (
          <div className="space-y-2">
            <Input value={block.url || ''} onChange={e => updateBlock(index, 'url', e.target.value)} placeholder="Image URL..." className="text-xs" />
            {block.url && <img src={block.url} alt={block.caption || ''} className="rounded-xl max-h-72 object-cover w-full" loading="lazy" />}
            <div className="grid grid-cols-2 gap-2">
              <Input value={block.caption || ''} onChange={e => updateBlock(index, 'caption', e.target.value)} placeholder="Caption..." className="text-xs" />
              <Input value={block.credit || ''} onChange={e => updateBlock(index, 'credit', e.target.value)} placeholder="Photo credit..." className="text-xs" />
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-2">
            <p className="text-xs text-[#5B616E] font-medium">Photo Gallery — {(block.images || []).length} images</p>
            {(block.images || []).map((img, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={img.url || ''} onChange={e => updateGalleryImage(index, i, 'url', e.target.value)} placeholder={`Image ${i + 1} URL...`} className="text-xs flex-1" />
                <Input value={img.caption || ''} onChange={e => updateGalleryImage(index, i, 'caption', e.target.value)} placeholder="Caption" className="text-xs w-32" />
                <button className="text-red-400 hover:text-red-600" onClick={() => {
                  const updated = [...blocks];
                  updated[index].images = (updated[index].images || []).filter((_, j) => j !== i);
                  onChange(updated);
                }}>×</button>
              </div>
            ))}
            {(block.images || []).filter(i => i.url).length > 0 && (
              <div className="grid grid-cols-3 gap-1 mt-1">
                {(block.images || []).filter(i => i.url).slice(0, 6).map((img, i) => (
                  <img key={i} src={img.url} alt={img.caption || ''} className="rounded-lg aspect-square object-cover" loading="lazy" />
                ))}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={() => addGalleryImage(index)} className="text-xs w-full">
              <Plus className="w-3 h-3 mr-1" /> Add Image to Gallery
            </Button>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-2">
            <Input value={block.url || ''} onChange={e => updateBlock(index, 'url', e.target.value)} placeholder="Video URL (YouTube, Vimeo, MP4, HLS .m3u8...)" />
            {block.url && (
              block.url.endsWith('.m3u8') || block.url.endsWith('.mp4') ? (
                <video src={block.url} controls className="w-full rounded-lg max-h-48" />
              ) : (
                <div className="aspect-video bg-[#121316] rounded-lg flex items-center justify-center text-white/50 text-sm">
                  Video preview: {block.url.slice(0, 50)}...
                </div>
              )
            )}
            <Input value={block.caption || ''} onChange={e => updateBlock(index, 'caption', e.target.value)} placeholder="Caption..." className="text-xs" />
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-2">
            <Input value={block.url || ''} onChange={e => updateBlock(index, 'url', e.target.value)} placeholder="Audio URL (MP3, WAV, OGG)..." />
            {block.url && <audio src={block.url} controls className="w-full" />}
            <Input value={block.caption || ''} onChange={e => updateBlock(index, 'caption', e.target.value)} placeholder="Caption..." className="text-xs" />
          </div>
        );

      case 'quote':
        return (
          <div className="space-y-2 pl-4 border-l-4 border-[#B89B5E]">
            <Textarea
              value={block.content || ''}
              onChange={e => updateBlock(index, 'content', e.target.value)}
              placeholder="Quote text..."
              className="border-0 focus-visible:ring-0 italic resize-none text-lg leading-relaxed"
              rows={3}
            />
            <Input value={block.attribution || ''} onChange={e => updateBlock(index, 'attribution', e.target.value)} placeholder="Attribution (name, title)..." className="text-xs" />
          </div>
        );

      case 'callout': {
        const variant = block.variant || 'info';
        const style = CALLOUT_STYLES[variant] || CALLOUT_STYLES.info;
        return (
          <div className="space-y-2">
            <Select value={variant} onValueChange={v => updateBlock(index, 'variant', v)}>
              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CALLOUT_STYLES).map(([k, s]) => <SelectItem key={k} value={k}>{s.icon} {k}</SelectItem>)}
              </SelectContent>
            </Select>
            <div style={{ background: style.bg, borderLeft: `4px solid ${style.border}`, padding: '12px 16px', borderRadius: '8px' }}>
              <Textarea
                value={block.content || ''}
                onChange={e => updateBlock(index, 'content', e.target.value)}
                placeholder="Callout content..."
                className="border-0 bg-transparent focus-visible:ring-0 resize-none text-sm"
                rows={2}
              />
            </div>
          </div>
        );
      }

      case 'embed':
        return (
          <div className="space-y-2">
            <Textarea
              value={block.content || ''}
              onChange={e => updateBlock(index, 'content', e.target.value)}
              placeholder="Paste embed code (Twitter, Instagram, YouTube, custom iframe)..."
              className="font-mono text-xs resize-none"
              rows={4}
            />
            {block.content && block.content.includes('<iframe') && (
              <div className="text-[10px] text-green-600">✓ iframe embed detected</div>
            )}
          </div>
        );

      case 'divider':
        return (
          <div className="py-2">
            <div className="border-t-2 border-[#E7E0D6]" />
          </div>
        );

      case 'data_viz':
        return (
          <div className="space-y-2">
            <Input value={block.title || ''} onChange={e => updateBlock(index, 'title', e.target.value)} placeholder="Chart title..." />
            <Select value={block.viz_type || 'bar'} onValueChange={v => updateBlock(index, 'viz_type', v)}>
              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['bar', 'line', 'pie', 'table'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea
              value={block.csv_data || ''}
              onChange={e => updateBlock(index, 'csv_data', e.target.value)}
              placeholder={"CSV data:\nlabel,value\nQ1,120\nQ2,240\nQ3,180"}
              className="font-mono text-xs resize-none"
              rows={5}
            />
          </div>
        );

      case 'related':
        return (
          <div className="space-y-2">
            <p className="text-xs text-[#5B616E]">Related article URLs (one per line)</p>
            <Textarea
              value={block.content || ''}
              onChange={e => updateBlock(index, 'content', e.target.value)}
              placeholder={"/article/article-slug-1\n/article/article-slug-2"}
              className="font-mono text-xs resize-none"
              rows={3}
            />
          </div>
        );

      default:
        return <Textarea value={block.content || ''} onChange={e => updateBlock(index, 'content', e.target.value)} placeholder={`${BLOCK_LABELS[block.type] || block.type} content...`} rows={3} />;
    }
  };

  return (
    <div className="space-y-2" data-testid="block-editor">
      {blocks.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-[#E7E0D6] rounded-xl">
          <Plus className="w-8 h-8 mx-auto mb-2 text-[#E7E0D6]" />
          <p className="text-sm text-[#5B616E] mb-3">Add your first block</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {['text', 'heading', 'image', 'gallery', 'quote', 'video'].map(type => {
              const Icon = BLOCK_ICONS[type];
              return (
                <Button key={type} variant="outline" size="sm" onClick={() => addBlock(type, -1)} className="text-xs">
                  <Icon className="w-3 h-3 mr-1" />{BLOCK_LABELS[type]}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {blocks.map((block, index) => {
        const Icon = BLOCK_ICONS[block.type] || Type;
        const isDraggingOver = dragOver === index;
        return (
          <div key={block.id || index} className="group relative">
            {/* Drop indicator */}
            {isDraggingOver && dragIndex !== null && dragIndex !== index && (
              <div className="h-1 bg-[#B89B5E] rounded-full mb-1 animate-pulse" />
            )}

            <Card
              className={`bg-white border-[#E7E0D6] transition-all ${isDraggingOver ? 'border-[#B89B5E] shadow-md' : 'hover:border-[#B89B5E]/30'} ${dragIndex === index ? 'opacity-40' : ''}`}
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={e => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{ cursor: 'default' }}
            >
              <CardContent className="pt-3 pb-3">
                <div className="flex items-start gap-2">
                  {/* Drag handle + arrow controls */}
                  <div className="flex flex-col items-center gap-0.5 pt-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <GripVertical className="w-4 h-4 text-[#B89B5E] cursor-grab active:cursor-grabbing" />
                    <button onClick={() => moveBlock(index, -1)} className="p-0.5 hover:bg-[#E7E0D6] rounded" title="Move up">
                      <ChevronUp className="w-3 h-3 text-[#5B616E]" />
                    </button>
                    <button onClick={() => moveBlock(index, 1)} className="p-0.5 hover:bg-[#E7E0D6] rounded" title="Move down">
                      <ChevronDown className="w-3 h-3 text-[#5B616E]" />
                    </button>
                  </div>

                  {/* Block content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 mb-1.5 text-[10px] text-[#5B616E]">
                      <Icon className="w-3 h-3" />
                      <span className="uppercase tracking-wider font-medium">{BLOCK_LABELS[block.type]}</span>
                    </div>
                    {renderBlockEditor(block, index)}
                  </div>

                  {/* Delete */}
                  <button
                    onClick={() => removeBlock(index)}
                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded transition-opacity flex-shrink-0"
                    title="Delete block"
                  >
                    <Trash2 className="w-3 h-3 text-[#B3261E]" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Add block button (between blocks) */}
            <div className="flex justify-center -my-1 relative z-10 h-3">
              <button
                onClick={() => setShowAddMenu(showAddMenu === index ? null : index)}
                className="w-6 h-6 rounded-full bg-[#E7E0D6] hover:bg-[#B89B5E] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                title="Add block"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>

            {/* Add block menu */}
            {showAddMenu === index && (
              <div className="absolute left-8 z-20 bg-white rounded-xl shadow-xl border border-[#E7E0D6] p-2 mt-1 grid grid-cols-3 gap-1 w-64">
                {Object.entries(BLOCK_ICONS).map(([type, BIcon]) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type, index)}
                    className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg hover:bg-[#FBF7EF] text-[10px] text-[#5B616E] hover:text-[#121316] transition-colors"
                  >
                    <BIcon className="w-4 h-4" />
                    {BLOCK_LABELS[type]}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Add block at end */}
      {blocks.length > 0 && (
        <div className="flex justify-center mt-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setShowAddMenu(showAddMenu === 'end' ? null : 'end')}
          >
            <Plus className="w-3 h-3 mr-1" /> Add Block
          </Button>
          {showAddMenu === 'end' && (
            <div className="absolute z-20 bg-white rounded-xl shadow-xl border border-[#E7E0D6] p-2 mt-8 grid grid-cols-3 gap-1 w-64">
              {Object.entries(BLOCK_ICONS).map(([type, BIcon]) => (
                <button
                  key={type}
                  onClick={() => addBlock(type, blocks.length - 1)}
                  className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg hover:bg-[#FBF7EF] text-[10px] text-[#5B616E] hover:text-[#121316] transition-colors"
                >
                  <BIcon className="w-4 h-4" />
                  {BLOCK_LABELS[type]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
